import {
  Autocomplete,
  Button,
  Icon,
  Text,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { CustomerBadge } from '@vend/react-utilities'
import { ApiCustomer, ApiCustomerGroup } from '@vend/types'
import { fetchApiJson, IncCounter } from '@vend/utilities'
import React, { forwardRef, useMemo, useRef, useState } from 'react'
import { store } from '../../../factories/database/database'
import {
  useTrainingModeManager,
  useWebRegisterStatusManager,
  useCustomerManager,
} from '../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../util/feature-cache'

interface Props {
  onSelect: (s: any) => void
  onAdd: () => void
  placeholder: string
  searchValue?: string
  onFetch?: (query: string) => void
}

interface SearchResultData {
  id: string
  type: string
}

interface SearchResult {
  data: SearchResultData[]
  related: {
    customers: Record<string, ApiCustomer>
  }
}

const useCustomerIndex = () => {
  const customerIndex = useMemo(() => {
    return store('customers').index('byCustomerCode')
  }, [])

  return customerIndex
}

const fetchErrorMessage = (isOnline: boolean) => {
  return (
    <div className="vd-mt4 vd-mb1 vd-mr1 vd-ml1">
      <div className="vd-mb4">
        <Text type="label">
          {isOnline ? (
            <T
              _str="Customer data can’t be searched."
              _context={'Customer search error message title, online'}
            />
          ) : (
            <T
              _str="Customer data can’t be searched in offline mode."
              _context={'Customer search error message title, offline'}
            />
          )}
        </Text>
      </div>
      <div className="vd-mb4">
        <Text type="body">
          <T
            _str="You can add a customer to the sale using their customer code or refresh the page to try searching again."
            _context={'Customer search error message body'}
          />
        </Text>
      </div>
      {isOnline && (
        <div>
          <Text type="supplementary">
            <T
              _str="Customer codes can be found in the customer’s page"
              _context={'Customer search error hint'}
            />
          </Text>
        </div>
      )}
    </div>
  )
}

const renderCustomerSuggestion = (
  customer: ApiCustomer & { customerGroup?: ApiCustomerGroup }
) => {
  return (
    <CustomerBadge
      apiCustomer={customer}
      size="x-small"
      group={customer.customerGroup}
    />
  )
}

type ActionBarProps = Pick<Props, 'onAdd'> & {
  close: () => void
  newCustomerCopy: string
}
const ActionBar = (props: ActionBarProps) => {
  const { onAdd, close, newCustomerCopy } = props

  return (
    <div className="vd-popover-list-footer vd-popover-list-footer--full">
      <Button
        className="vd-popover-list-footer-action"
        onClick={() => {
          close()
          onAdd()
        }}
        data-track="add-as-new-customer"
      >
        <Icon icon="fa-plus" className="vd-mr1" />
        {newCustomerCopy}
      </Button>
    </div>
  )
}
export const CustomerSearch = forwardRef<Autocomplete<any>, Props>(
  ({ onSelect, onAdd, onFetch, placeholder, searchValue = '' }, ref) => {
    const t = useT()
    const webRegisterStatusManager = useWebRegisterStatusManager()
    const trainingModeManager = useTrainingModeManager()
    const customerManager = useCustomerManager()
    const [inputValue, setInputValue] = useState<string>(searchValue.trim())
    const lastSearchResult = useRef<ApiCustomer[]>([])
    const customerIndex = useCustomerIndex()
    const [fetchError, setFetchError] = useState<React.ReactNode>(null)
    const ffCustomerGroupsEnabled = getFeatureFromCache('customer_groups')

    const getCustomerGroup = (customer: ApiCustomer) => {
      if (!ffCustomerGroupsEnabled) {
        return customer
      }
      return customerManager
        .getCustomerGroup(customer)
        .then(customerGroup => {
          return customerGroup ? { ...customer, customerGroup } : customer
        })
        .catch(() => customer)
    }
    const fetchCustomers = async (query: string) => {
      setFetchError(null)

      const trimmedQuery = query.trim()
      setInputValue(trimmedQuery)

      try {
        const { data, related }: SearchResult = await fetchApiJson(
          `/api/2.0/suggest?type=customer&page_size=30&include_related=true&q=${trimmedQuery}`
        )

        const customers = data.map(suggestion => {
          return related.customers[suggestion.id]
        })
        const customersWithCustomerGroup = await Promise.all(
          customers.map(customer => getCustomerGroup(customer))
        )

        lastSearchResult.current = customersWithCustomerGroup

        return customersWithCustomerGroup
      } catch {
        setFetchError(fetchErrorMessage(webRegisterStatusManager.isOnline()))
        return []
      } finally {
        onFetch?.(trimmedQuery)
      }
    }

    const feelingLucky = async (query: string) => {
      if (lastSearchResult.current.length > 0) {
        // In this case we let the autocomplete handle the selection
        return
      }

      // Check if we have an exact match for a customer code
      const idbCustomers = await customerIndex.getAll(query)
      if (idbCustomers.length === 1) {
        IncCounter('webregister.customer_code_search', 1, {
          online: String(webRegisterStatusManager.isOnline()),
        })

        const customerWithCustomerGroup = await getCustomerGroup(
          idbCustomers[0]
        )

        return customerWithCustomerGroup
      } else if (idbCustomers.length > 1) {
        ToastNotifications.negative(
          <T
            _str="More than 1 result found for “{query}”. Try typing that into the search field to see results."
            query={query}
            _context={'Error message when searching for customers'}
          />
        )
        return
      }

      if (
        webRegisterStatusManager.isOnline() &&
        !trainingModeManager.trainingModeEnabled &&
        !fetchError
      ) {
        // Haven't matched a customer and are able to add one
        onAdd()
        return
      }

      // Give up and inform the user we can't find the customer
      ToastNotifications.negative(
        <T
          _str="No results found for “{query}”. Try searching by customer name."
          query={query}
          _context={'Error message when searching for customers'}
        />
      )
      return
    }

    const newCustomerCopy = inputValue
      ? t('Add "{customer}" as a new customer', {
          _context: 'Text for customer step in quote modal.',
          customer: inputValue,
        })
      : t('Add a new customer', {
          _context: 'Input placeholder text for customer step in quote modal.',
        })

    return (
      <Autocomplete
        ref={ref}
        leftIcon="fa fa-user"
        fetchSuggestions={fetchCustomers}
        onChange={onSelect}
        renderSuggestion={renderCustomerSuggestion}
        placeholder={placeholder}
        feelingLucky={feelingLucky}
        selectedValue={searchValue}
        renderError={fetchError}
        actionBar={
          !fetchError
            ? ({ close }) => (
                <ActionBar
                  onAdd={onAdd}
                  close={close}
                  newCustomerCopy={newCustomerCopy}
                />
              )
            : null
        }
      />
    )
  }
)
